import { hmac } from "@noble/hashes/hmac.js";
import { sha256 } from "@noble/hashes/sha2.js";
import { concatBytes, utf8ToBytes } from "@noble/hashes/utils.js";
import { bytesEqual, dh, generateKeyPair, type KeyPair } from "./keys";

/**
 * Proof that a daemon holds the private key behind the address it registers under,
 * so nobody can squat on (and block) someone else's daemon address at the relay.
 *
 * The relay makes a fresh ephemeral keypair per connection and sends the public half
 * plus a nonce. The daemon computes DH(its secret, relay ephemeral public) and returns
 * HMAC(that, nonce || its address). The relay computes the same DH from the other side
 * and checks the MAC. Only the holder of the daemon's secret key can produce it, and the
 * fresh ephemeral + nonce make it useless if replayed on another connection.
 * No signature scheme is needed, just X25519 + HMAC, which we already depend on.
 */

const LABEL = utf8ToBytes("foreman/v1/relay-register");

export interface RegistrationChallenge {
  /** Relay-side secret for this connection. Never sent. */
  ephemeral: KeyPair;
  nonce: Uint8Array;
}

export function createChallenge(randomNonce: Uint8Array): RegistrationChallenge {
  return { ephemeral: generateKeyPair(), nonce: randomNonce };
}

/** Daemon side. */
export function createRegistrationProof(
  daemon: KeyPair,
  relayEphemeralPublic: Uint8Array,
  nonce: Uint8Array,
): Uint8Array {
  return mac(dh(daemon.secretKey, relayEphemeralPublic), nonce, daemon.publicKey);
}

/** Relay side. `claimedPublicKey` is the address the daemon wants to register as. */
export function verifyRegistrationProof(
  challenge: RegistrationChallenge,
  claimedPublicKey: Uint8Array,
  proof: Uint8Array,
): boolean {
  if (claimedPublicKey.length !== 32 || proof.length !== 32) return false;
  let expected: Uint8Array;
  try {
    expected = mac(dh(challenge.ephemeral.secretKey, claimedPublicKey), challenge.nonce, claimedPublicKey);
  } catch {
    return false; // e.g. a low-order point as the claimed key
  }
  return bytesEqual(expected, proof);
}

function mac(sharedSecret: Uint8Array, nonce: Uint8Array, daemonPublic: Uint8Array): Uint8Array {
  return hmac(sha256, sharedSecret, concatBytes(LABEL, nonce, daemonPublic));
}
